import React, { useEffect, useState } from "react";
import { FileText, MapPin } from "lucide-react";

/* A drafted post, typed out as if somebody were writing it.

   Used by both signup composers to show a post the moment it exists, in the
   shape it will have on the board, rather than as a form to fill in. Typing
   does two jobs: it covers the seconds the draft is still on its way, and it
   makes the result read as written for them, which is the point.

   Only real text is ever typed. While the post is on its way the card types
   the header it was given (a name and a city we already have) and then sits on
   "Writing your post" with a cursor. It never types placeholder words that get
   swapped out, which would read as fake the moment anybody noticed. */

// Milliseconds per character. The header is slow on purpose: it is what is on
// screen while the post is still being written.
const HEADER_MS = 70;
const TITLE_MS = 35;
const BODY_MS = 9;

// The card shows the start of the description, not all of it. The full text
// is one tap away; here it would push the next step off a phone screen.
const BODY_PREVIEW_CHARS = 220;

function prefersReducedMotion() {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

function previewBody(text) {
  const clean = (text || "").trim();
  if (clean.length <= BODY_PREVIEW_CHARS) return clean;
  const cut = clean.slice(0, BODY_PREVIEW_CHARS);
  const space = cut.lastIndexOf(" ");
  return `${cut.slice(0, space > 120 ? space : BODY_PREVIEW_CHARS).trim()}…`;
}

/* How much of `text` is showing, advancing one character per `ms` once
   `active`. Follows the text if it changes underneath (a live name field, or
   an edit), carrying on from where it had got to. */
function useTyped(text, ms, active, instant) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    if (instant) { setShown(text.length); return undefined; }
    if (shown >= text.length) return undefined;
    const id = setTimeout(() => setShown((n) => n + 1), ms);
    return () => clearTimeout(id);
  }, [text, ms, active, instant, shown]);
  return { value: text.slice(0, shown), done: shown >= text.length };
}

function Cursor() {
  return (
    <span
      aria-hidden="true"
      className="inline-block w-[2px] h-[1em] -mb-[2px] ml-[1px] bg-brand animate-pulse align-baseline"
    />
  );
}

/**
 * @param name      Shown in the header and as initials. Optional.
 * @param location  Shown in the header with a pin. Optional.
 * @param post      undefined while it is being written, else {title, description}.
 * @param instant   Show everything at once (after an edit, say).
 * @param onTyped   Called once, when the whole card has finished typing.
 */
export default function TypedPostCard({ name = "", location = "", post, instant = false, onTyped }) {
  // Typed once is enough. After that, a change underneath (a location picked,
  // a name corrected) shows straight away: retyping the header would hide the
  // post below it until the header caught up.
  const [settled, setSettled] = useState(false);
  const still = instant || settled || prefersReducedMotion();

  const who = name.trim();
  const where = (location || "").trim();
  const headerText = [who, where].filter(Boolean).join("\u0000");
  const h = useTyped(headerText, HEADER_MS, true, still);
  const ready = !!post && h.done;
  const title = useTyped(post?.title || "", TITLE_MS, ready, still);
  const body = useTyped(previewBody(post?.description), BODY_MS, ready && title.done, still);

  const finished = ready && title.done && body.done;
  useEffect(() => {
    if (!finished) return;
    setSettled(true);
    if (onTyped) onTyped();
    // Fires on the transition only; onTyped is not expected to be stable.
  }, [finished]); // eslint-disable-line react-hooks/exhaustive-deps

  // The header is typed as one string, then split back into its two parts so
  // the city can sit beside its pin.
  const [typedWho, typedWhere = ""] = who
    ? h.value.split("\u0000")
    : ["", h.value];
  const initials = who.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

  return (
    <div
      className="rounded-xl border border-brand/30 bg-surface px-4 py-3.5 shadow-sift-sm"
      aria-live="polite"
      aria-busy={!finished}
    >
      {(who || where) && (
        <div className="flex items-center gap-2.5 mb-3">
          {who && (
            <span className="h-8 w-8 rounded-full bg-brand-tint text-brand-ink text-xs font-bold grid place-items-center flex-shrink-0">
              {initials || <FileText size={14} strokeWidth={1.8} aria-hidden="true" />}
            </span>
          )}
          <span className="min-w-0 text-xs text-secondary break-words">
            {typedWho}
            {typedWhere && (
              <span className={"inline-flex items-center gap-0.5" + (who ? " ml-2" : "")}>
                <MapPin size={11} strokeWidth={2} aria-hidden="true" />
                {typedWhere}
              </span>
            )}
            {!h.done && <Cursor />}
          </span>
        </div>
      )}

      {h.done && !post && (
        <p className="text-sm text-faint">
          Writing your post<Cursor />
        </p>
      )}

      {ready && (
        <>
          <p className="text-ink text-sm font-semibold leading-snug">
            {title.value}
            {!title.done && <Cursor />}
          </p>
          {title.done && body.value && (
            <p className="mt-1.5 text-secondary text-xs leading-relaxed">
              {body.value}
              {!body.done && <Cursor />}
            </p>
          )}
        </>
      )}
    </div>
  );
}
